import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import {
  apiFetch,
  ApiError,
  HUB_GATEWAY_DOWN_MESSAGE,
  HUB_UNREACHABLE_MESSAGE,
  isProductionInfrastructureFailure,
} from "@/lib/api";

export type TabletPinSetupPanelProps = {
  signedInEmail: string;
  /** When false, show guidance instead of the form (wrong Google domain). */
  workspaceAccount: boolean;
  className?: string;
};

function formatTabletPinSaveError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 0) {
      return HUB_UNREACHABLE_MESSAGE;
    }
    if (isProductionInfrastructureFailure(error.status, error.body)) {
      return HUB_GATEWAY_DOWN_MESSAGE;
    }
    return error.message;
  }
  if (error instanceof Error && (error.message === "Failed to fetch" || error.message === "fetch failed")) {
    return HUB_UNREACHABLE_MESSAGE;
  }
  return error instanceof Error ? error.message : "Could not save PIN.";
}

export function TabletPinSetupPanel({
  signedInEmail,
  workspaceAccount,
  className,
}: TabletPinSetupPanelProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  const handleSavePin = async (): Promise<void> => {
    if (pin.length < 4 || pin.length > 8) {
      setError("PIN must be 4 to 8 digits.");
      return;
    }
    if (!/^\d+$/.test(pin)) {
      setError("PIN must contain digits only.");
      return;
    }
    if (pin !== confirmPin) {
      setError("PIN and confirmation do not match.");
      return;
    }

    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await apiFetch<{ ok: boolean; message: string }>("/api/mdm/tablet-pin", {
        method: "POST",
        body: { pin },
      });
      setMessage(result.message);
      setPin("");
      setConfirmPin("");
    } catch (saveError) {
      setError(formatTabletPinSaveError(saveError));
    } finally {
      setBusy(false);
    }
  };

  if (!workspaceAccount) {
    return (
      <div
        className={
          className ??
          "rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        }
      >
        Signed in as {signedInEmail || "unknown"}. Use a Smart Grid Solutions Google account to set a
        manufacturing tablet PIN.
      </div>
    );
  }

  return (
    <div className={className}>
      <p className="text-sm text-slate-600">
        Signed in as <span className="font-medium text-slate-900">{signedInEmail}</span>. Choose a PIN you
        will enter on shared manufacturing floor tablets.
      </p>
      {error ? (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}
      {message ? (
        <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          {message}
        </div>
      ) : null}
      <div className="mt-5 space-y-4">
        <div>
          <Label htmlFor="tablet-pin">PIN</Label>
          <Input
            id="tablet-pin"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(event) => setPin(event.target.value)}
            className="mt-1 min-h-12"
          />
        </div>
        <div>
          <Label htmlFor="tablet-pin-confirm">Confirm PIN</Label>
          <Input
            id="tablet-pin-confirm"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={confirmPin}
            onChange={(event) => setConfirmPin(event.target.value)}
            className="mt-1 min-h-12"
          />
        </div>
        <Button className="min-h-12 w-full" onClick={() => void handleSavePin()} isLoading={busy}>
          Save tablet PIN
        </Button>
      </div>
    </div>
  );
}
